/**
 * Review wizard — Intent → Orchestrate → Output → [Artefacts] → Submit.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import { Button } from "@/components/ui/button/Button";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { agentReviewAPI, parseApiErrorMessage } from "@/services/api/agentReview";
import { DisabledActionTooltip } from "./DisabledActionTooltip";
import {
  saveReviewBlockedReasons,
  wizardNextBlockedReasons,
} from "./disabledActionReasons";
import type {
  AceProfile,
  AgentRun,
  ArtefactRatings,
  CheckAnswer,
  PhaseCriteria,
  PhaseRatings,
  Rag,
  ReviewComparison,
  ReviewPhase,
  ReviewRecord,
} from "./types";
import {
  applyCheatSheetVariantAutoNaForRun,
} from "./cheatSheetVariantChecks";
import {
  artefactRatingsFromReview,
  initialArtefactRatings,
  phaseRatingsFromReview,
} from "./reviewHydration";
import { resumeWizardState } from "./resumeWizardState";
import {
  hasPartialArtefactRatings,
  persistedArtefactsComplete,
  reviewIsDirty,
  reviewNeedsSave,
} from "./reviewPersistence";
import { splitRunPhases } from "./phases";
import { deriveRag } from "./deriveRag";
import { failCommentsComplete } from "./checkComments";
import { IntentPhase } from "./IntentPhase";
import { OrchestrationPhase } from "./OrchestrationPhase";
import { OutcomePhase } from "./OutcomePhase";
import { ArtefactsPhase, artefactRatingsComplete } from "./ArtefactsPhase";
import { RatingPanel } from "./RatingPanel";
import { SummaryScreen } from "./SummaryScreen";
import { ReviewCaseHeader } from "./ReviewCaseHeader";
import { PhaseInstruction } from "./PhaseInstruction";
import {
  WIZARD_STEP_LABELS,
  type WizardStep,
  wizardStepsForRun,
  nextWizardStep,
  prevWizardStep,
} from "./wizardSteps";
import { artefactWizardNextLabel } from "./artefactReviewCopy";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import { clearReviewDraft, loadReviewDraft, saveReviewDraft } from "./reviewDraftStorage";
import type { ReviewCaseOpenIntent } from "./reviewCaseOpenIntent";

const NO_BLOCKED_REASONS: string[] = [];

interface SessionStats {
  reviewed: number;
  total: number;
}

interface ReviewWizardProps {
  run: AgentRun;
  caseLabel?: string;
  catalogueKey?: string | null;
  sessionStats?: SessionStats;
  existingReview?: ReviewRecord | null;
  initialOpenIntent?: ReviewCaseOpenIntent | null;
  onOpenIntentHandled?: () => void;
  onDone: () => void;
  onSubmit: (ratings: PhaseRatings, artefactRatings: ArtefactRatings) => Promise<ReviewRecord>;
}

function emptyRating() {
  return { answers: {}, check_comments: {}, suggested_rag: null, human_rag: null, human_note: "" };
}

function phaseComplete(criteria: PhaseCriteria, rating: PhaseRatings[ReviewPhase]): boolean {
  if (rating.human_rag === null) return false;
  if (!failCommentsComplete(criteria, rating.answers, rating.check_comments)) return false;
  const derived = deriveRag(criteria, rating.answers);
  const overridden = derived.rag !== null && rating.human_rag !== derived.rag;
  const needsNote = rating.human_rag === "amber" || rating.human_rag === "red" || overridden;
  return !needsNote || rating.human_note.trim().length > 0;
}

function resolveOpenStep(
  resumedStep: WizardStep,
  openIntent: ReviewCaseOpenIntent | null | undefined,
  hasArtefacts: boolean,
): WizardStep {
  if (openIntent?.viewSubmitted) {
    if (openIntent.focusArtefactId && hasArtefacts) return "artefacts";
    return "submit";
  }
  if (openIntent?.focusPhase === "orchestration") return "orchestrate";
  if (openIntent?.focusPhase === "output") return "output";
  return resumedStep;
}

function wizardInitialState(
  run: AgentRun,
  existingReview: ReviewRecord | null | undefined,
  openIntent?: ReviewCaseOpenIntent | null,
) {
  const hasArtefacts = (run.artefacts?.length ?? 0) > 0;
  const artefactIds = (run.artefacts ?? []).map((a) => a.artifact_id);

  if (existingReview) {
    const resumed = resumeWizardState(existingReview, hasArtefacts, artefactIds, run);
    return {
      step: resolveOpenStep(resumed.step, openIntent, hasArtefacts),
      ratings: resumed.ratings,
      artefactRatings: resumed.artefactRatings,
      intentConfirmed: resumed.intentConfirmed,
      intentFeedback: "",
    };
  }
  const draft = loadReviewDraft(run.run_id);
  if (draft) {
    return {
      step: draft.step,
      ratings: draft.ratings,
      artefactRatings: applyCheatSheetVariantAutoNaForRun(run, draft.artefactRatings),
      intentConfirmed: draft.intentConfirmed,
      intentFeedback: draft.intentFeedback,
    };
  }
  return {
    step: "intent" as WizardStep,
    ratings: { orchestration: emptyRating(), output: emptyRating() },
    artefactRatings: initialArtefactRatings(run),
    intentConfirmed: false,
    intentFeedback: "",
  };
}

export function ReviewWizard({
  run,
  caseLabel,
  catalogueKey,
  sessionStats,
  existingReview,
  initialOpenIntent = null,
  onOpenIntentHandled,
  onDone,
  onSubmit,
}: ReviewWizardProps) {
  const isAdmin = useIsAdmin();
  const phases = useMemo(() => splitRunPhases(run), [run]);
  const criteria = run.review_criteria ?? null;
  const resumed = Boolean(existingReview);
  const hasArtefacts = (run.artefacts?.length ?? 0) > 0;
  const artefactCount = run.artefacts?.length ?? 0;
  const wizardSteps = useMemo(() => wizardStepsForRun(hasArtefacts), [hasArtefacts]);

  const artefactIds = useMemo(() => (run.artefacts ?? []).map((a) => a.artifact_id), [run.artefacts]);

  const initialState = useRef(
    wizardInitialState(run, existingReview, initialOpenIntent),
  );
  const [focusCheckId, setFocusCheckId] = useState<string | null>(
    () => initialOpenIntent?.focusCheckId ?? null,
  );
  const [focusArtefactId, setFocusArtefactId] = useState<string | null>(
    () => initialOpenIntent?.focusArtefactId ?? null,
  );

  const [step, setStep] = useState<WizardStep>(() => initialState.current.step);
  const [ratings, setRatings] = useState<PhaseRatings>(() => initialState.current.ratings);
  const [artefactRatings, setArtefactRatings] = useState<ArtefactRatings>(
    () => initialState.current.artefactRatings,
  );
  const [savedReview, setSavedReview] = useState<ReviewRecord | null>(() => existingReview ?? null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reviewId, setReviewId] = useState<string | null>(() => existingReview?.review_id ?? null);
  const [versionNumber, setVersionNumber] = useState<number>(() => existingReview?.version_number ?? 1);
  const [versionRefreshToken, setVersionRefreshToken] = useState(0);
  const [aceTrialRefreshToken, setAceTrialRefreshToken] = useState(0);
  const [aceComparison, setAceComparison] = useState<ReviewComparison | null>(null);
  const [aceLoading, setAceLoading] = useState(false);
  const [aceError, setAceError] = useState<string | null>(null);
  const [intentConfirmed, setIntentConfirmed] = useState(() => initialState.current.intentConfirmed);
  const [intentFeedback, setIntentFeedback] = useState(() => initialState.current.intentFeedback);
  const aceRunRef = useRef<Promise<void> | null>(null);
  const hydratedRunIdRef = useRef<string | null>(null);

  const nextLabels: Partial<Record<WizardStep, string>> = {
    intent: "See what the agent did",
    orchestrate: "See the outcome",
    output: hasArtefacts ? artefactWizardNextLabel(run.artefacts, run.catalogue_key) : "Review and submit",
    artefacts: "Review and submit",
  };

  useEffect(() => {
    hydratedRunIdRef.current = null;
  }, [run.run_id]);

  useEffect(() => {
    saveReviewDraft(run.run_id, {
      step,
      ratings,
      artefactRatings,
      intentConfirmed,
      intentFeedback,
    });
  }, [run.run_id, step, ratings, artefactRatings, intentConfirmed, intentFeedback]);

  useEffect(() => {
    if (!existingReview || hydratedRunIdRef.current === run.run_id) return;
    clearReviewDraft(run.run_id);
    const state = resumeWizardState(existingReview, hasArtefacts, artefactIds, run);
    setStep(resolveOpenStep(state.step, initialOpenIntent, hasArtefacts));
    setRatings(state.ratings);
    setArtefactRatings(state.artefactRatings);
    setIntentConfirmed(state.intentConfirmed);
    setReviewId(state.reviewId);
    setVersionNumber(existingReview.version_number ?? 1);
    setSavedReview(existingReview);
    if (initialOpenIntent?.focusCheckId) {
      setFocusCheckId(initialOpenIntent.focusCheckId);
    }
    if (initialOpenIntent?.focusArtefactId) {
      setFocusArtefactId(initialOpenIntent.focusArtefactId);
    }
    hydratedRunIdRef.current = run.run_id;
    onOpenIntentHandled?.();
  }, [
    existingReview,
    hasArtefacts,
    artefactIds,
    run.run_id,
    initialOpenIntent,
    onOpenIntentHandled,
  ]);

  useEffect(() => {
    if (!isAdmin || !existingReview?.review_id || !existingReview.ace_rating) return;
    void agentReviewAPI
      .getReviewComparison(existingReview.review_id)
      .then(setAceComparison)
      .catch(() => {
        /* comparison is optional on resume */
      });
  }, [existingReview, isAdmin]);

  const agentName = run.agent_name?.trim() || "Agent";

  const activePhase: ReviewPhase | null =
    step === "orchestrate" ? "orchestration" : step === "output" ? "output" : null;
  const phaseCriteria = activePhase && criteria ? criteria[activePhase] : null;

  const phaseDone = useMemo(
    () => ({
      orchestration: criteria ? phaseComplete(criteria.orchestration, ratings.orchestration) : false,
      output: criteria ? phaseComplete(criteria.output, ratings.output) : false,
    }),
    [criteria, ratings],
  );

  const artefactsDone = useMemo(
    () => artefactRatingsComplete(run, artefactRatings),
    [run, artefactRatings],
  );

  const needsSave = useMemo(
    () => reviewNeedsSave(run, savedReview, ratings, artefactRatings, artefactsDone, submitted),
    [run, savedReview, ratings, artefactRatings, artefactsDone, submitted],
  );

  const canVisitStep = (target: WizardStep): boolean =>
    target === "intent" || intentConfirmed || resumed;

  const goToStep = async (target: WizardStep) => {
    if (!canVisitStep(target)) return;

    if (
      target === "submit" &&
      step !== "submit" &&
      phaseDone.orchestration &&
      phaseDone.output &&
      needsSave &&
      (artefactsDone || hasPartialArtefactRatings(run, artefactRatings))
    ) {
      const record = await persistReview();
      if (!record) return;
    }

    setStep(target);
  };

  const goToArtefactCompare = (artifactId: string) => {
    setFocusArtefactId(artifactId);
    goToStep("artefacts");
  };

  const handleArtefactRatingChange = useCallback(
    (
      artifactId: string,
      ratingOrUpdater:
        | ArtefactRatings[string]
        | ((prev: ArtefactRatings[string] | undefined) => ArtefactRatings[string]),
    ) => {
      setArtefactRatings((prev) => {
        const nextRating =
          typeof ratingOrUpdater === "function"
            ? ratingOrUpdater(prev[artifactId])
            : ratingOrUpdater;
        if (prev[artifactId] === nextRating) return prev;
        return { ...prev, [artifactId]: nextRating };
      });
    },
    [],
  );

  const showAceFeatures = isAdmin && (submitted || resumed);

  const advance = (target: WizardStep) => {
    setStep(target);
  };

  const setAnswer = (checkId: string, answer: CheckAnswer) => {
    if (!activePhase || !phaseCriteria) return;
    setRatings((prev) => {
      const phase = prev[activePhase];
      const answers = { ...phase.answers, [checkId]: answer };
      const check_comments =
        answer !== "fail"
          ? { ...phase.check_comments, [checkId]: "" }
          : phase.check_comments;
      const derived = deriveRag(phaseCriteria, answers);
      const wasFollowingSuggestion = phase.human_rag === null || phase.human_rag === phase.suggested_rag;
      return {
        ...prev,
        [activePhase]: {
          ...phase,
          answers,
          check_comments,
          suggested_rag: derived.rag,
          human_rag: wasFollowingSuggestion ? derived.rag : phase.human_rag,
        },
      };
    });
  };

  const setCheckComment = (checkId: string, comment: string) => {
    if (!activePhase) return;
    setRatings((prev) => ({
      ...prev,
      [activePhase]: {
        ...prev[activePhase],
        check_comments: { ...prev[activePhase].check_comments, [checkId]: comment },
      },
    }));
  };

  const setOverride = (rag: Rag) => {
    if (!activePhase) return;
    setRatings((prev) => ({ ...prev, [activePhase]: { ...prev[activePhase], human_rag: rag } }));
  };

  const setNote = (note: string) => {
    if (!activePhase) return;
    setRatings((prev) => ({ ...prev, [activePhase]: { ...prev[activePhase], human_note: note } }));
  };

  const persistReview = async (): Promise<ReviewRecord | null> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const record = await onSubmit(ratings, artefactRatings);
      clearReviewDraft(run.run_id);
      setSavedReview(record);
      setArtefactRatings(artefactRatingsFromReview(record, artefactIds, run));
      setReviewId(record.review_id);
      setVersionNumber(record.version_number ?? 1);
      setVersionRefreshToken((n) => n + 1);
      setSubmitted(true);
      if (isAdmin && artefactRatingsComplete(run, artefactRatings)) {
        void runAceRating(record.review_id, false);
      }
      return record;
    } catch {
      setSubmitError("Couldn't save your review. Please try again.");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    await persistReview();
  };

  const formatAceError = async (
    err: unknown,
    fallback: string,
    hasExistingRating: boolean,
  ): Promise<string> => {
    const status =
      err && typeof err === "object" && "response" in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
    let detail = await parseApiErrorMessage(err, fallback);
    if (status === 504 || status === 408) {
      detail =
        artefactCount > 0
          ? `Ace timed out — ${artefactCount} LOA${artefactCount === 1 ? "" : "s"} can take several minutes. Try again.`
          : "Ace timed out on this run. Try again in a moment.";
    }
    if (hasExistingRating) {
      return `Couldn't refresh Ace — still showing your last rating. ${detail}`;
    }
    return detail;
  };

  const runAceRating = async (reviewIdOverride?: string | null, force = false) => {
    const id = reviewIdOverride ?? reviewId;
    if (!id) return;
    if ((run.artefacts?.length ?? 0) > 0 && !artefactRatingsComplete(run, artefactRatings)) {
      return;
    }
    if (aceRunRef.current) {
      await aceRunRef.current;
      return;
    }

    const task = (async () => {
      const hadExistingRating = Boolean(aceComparison?.ace_rating);
      setAceLoading(true);
      setAceError(null);
      try {
        setAceComparison(await agentReviewAPI.rateWithAce(id, force, null, artefactCount));
        setAceTrialRefreshToken((n) => n + 1);
        setVersionRefreshToken((n) => n + 1);
        setVersionNumber((n) => n + 1);
      } catch (err: unknown) {
        setAceError(await formatAceError(err, "Ace couldn't rate this run. Try again in a moment.", hadExistingRating));
      } finally {
        setAceLoading(false);
      }
    })();

    aceRunRef.current = task;
    try {
      await task;
    } finally {
      aceRunRef.current = null;
    }
  };

  const runFreshAce = async (args: {
    criteriaVersionId: string | null;
    aceProfile: AceProfile;
    note: string;
    promote: boolean;
  }) => {
    if (!reviewId) return;
    if (aceRunRef.current) {
      await aceRunRef.current;
      return;
    }

    const task = (async () => {
      const hadExistingRating = Boolean(aceComparison?.ace_rating);
      setAceLoading(true);
      setAceError(null);
      try {
        const comparison = await agentReviewAPI.runAceTrial(reviewId, {
          criteriaVersionId: args.criteriaVersionId,
          aceProfile: args.aceProfile,
          note: args.note,
          promote: args.promote,
          artefactCount,
        });
        setAceComparison(comparison);
        setAceTrialRefreshToken((n) => n + 1);
        if (args.promote) {
          setVersionRefreshToken((n) => n + 1);
          setVersionNumber((n) => n + 1);
        }
      } catch (err: unknown) {
        setAceError(
          await formatAceError(
            err,
            "Ace couldn't complete this fresh run. Try again in a moment.",
            hadExistingRating,
          ),
        );
      } finally {
        setAceLoading(false);
      }
    })();

    aceRunRef.current = task;
    try {
      await task;
    } finally {
      aceRunRef.current = null;
    }
  };

  const selectAceTrial = async (trialId: string) => {
    if (!reviewId) return;
    setAceLoading(true);
    setAceError(null);
    try {
      setAceComparison(await agentReviewAPI.getAceTrialComparison(reviewId, trialId));
    } catch {
      setAceError("Couldn't load that Ace trial.");
    } finally {
      setAceLoading(false);
    }
  };

  const promoteAceTrial = async (trialId: string) => {
    if (!reviewId) return;
    setAceLoading(true);
    setAceError(null);
    try {
      setAceComparison(await agentReviewAPI.promoteAceTrial(reviewId, trialId));
      setAceTrialRefreshToken((n) => n + 1);
      setVersionRefreshToken((n) => n + 1);
      setVersionNumber((n) => n + 1);
    } catch {
      setAceError("Couldn't promote that Ace trial.");
    } finally {
      setAceLoading(false);
    }
  };

  const currentComplete =
    (step === "intent" && intentConfirmed) ||
    (step === "artefacts" && artefactsDone) ||
    (!!activePhase && !!phaseCriteria && phaseComplete(phaseCriteria, ratings[activePhase]));

  const nextBlockedReasons = useMemo(
    () =>
      wizardNextBlockedReasons({
        step,
        intentConfirmed,
        submitting,
        criteria: phaseCriteria,
        phaseRating: activePhase ? ratings[activePhase] : null,
        run,
        artefactRatings,
      }),
    [step, intentConfirmed, submitting, phaseCriteria, activePhase, ratings, run, artefactRatings],
  );

  const handleNext = async () => {
    const next = nextWizardStep(step, hasArtefacts);
    if (!next) return;

    if (next === "submit" && phaseDone.orchestration && phaseDone.output && needsSave) {
      if (artefactsDone || hasPartialArtefactRatings(run, artefactRatings)) {
        const record = await persistReview();
        if (!record) return;
      }
    }

    advance(next);
  };

  const reviewSaved = (submitted || Boolean(savedReview)) && !needsSave;
  const phasesRated =
    ratings.orchestration.human_rag !== null && ratings.output.human_rag !== null;
  const hasPartialArtefacts = hasPartialArtefactRatings(run, artefactRatings);
  const canSaveReview =
    phasesRated &&
    (artefactsDone || !hasArtefacts || (Boolean(savedReview) && hasPartialArtefacts));

  const saveBlockedReasons = useMemo(
    () =>
      saveReviewBlockedReasons({
        submitting,
        ratings,
        criteria,
        hasArtefacts,
        artefactsDone,
        hasSavedReview: Boolean(savedReview),
        hasPartialArtefacts,
        run,
        artefactRatings,
      }),
    [
      submitting,
      ratings,
      criteria,
      hasArtefacts,
      artefactsDone,
      savedReview,
      hasPartialArtefacts,
      run,
      artefactRatings,
    ],
  );

  const handleBack = () => {
    if (submitted || step === "intent") {
      onDone();
      return;
    }
    const prev = prevWizardStep(step, hasArtefacts);
    if (prev) setStep(prev);
  };

  if (!criteria) {
    return (
      <div className={clsx("flex flex-col h-full w-full p-4", WORKBENCH_ROOT_TEXT_CLASS)}>
        <div className={clsx("rounded-2xl px-6 py-5 space-y-4", WORKBENCH_SURFACE_CLASS)}>
          {run.task_name ? <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{run.task_name}</p> : null}
          <p className="text-sm text-rose-300">
            This task has no review criteria configured. Ask an engineer to add review guidance to the
            task catalogue.
          </p>
          <button
            type="button"
            onClick={onDone}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to list
          </button>
        </div>
      </div>
    );
  }

  const artefactsTabDone =
    !hasArtefacts ||
    (savedReview
      ? persistedArtefactsComplete(run, savedReview) &&
        !reviewIsDirty(run, savedReview, ratings, artefactRatings)
      : artefactsDone);

  const tabDone: Partial<Record<WizardStep, boolean>> = {
    intent: intentConfirmed || resumed,
    orchestrate: resumed || phaseDone.orchestration,
    output: resumed || phaseDone.output,
    artefacts: artefactsTabDone,
    submit: (savedReview ? persistedArtefactsComplete(run, savedReview) : artefactsDone) && !needsSave,
  };

  return (
    <div className={clsx("flex flex-col h-full min-h-0 w-full p-4", WORKBENCH_ROOT_TEXT_CLASS)} data-testid="review-wizard">
      <div className={clsx("relative flex flex-1 min-h-0 flex-col overflow-hidden rounded-2xl", WORKBENCH_SURFACE_CLASS)}>
        <div className="px-6 pt-4 pb-3 flex-shrink-0">
          <ReviewCaseHeader
            caseLabel={caseLabel ?? run.subject_label}
            plannrAccountUuid={run.plannr_account_uuid}
            taskName={run.task_name}
            agentName={agentName !== "Agent" ? agentName : undefined}
            sessionReviewed={sessionStats?.reviewed}
            sessionTotal={sessionStats?.total}
          />
        </div>

        <div className="px-6 flex-shrink-0">
          <SegmentedTabs
            className="mt-1 -mb-px overflow-x-auto"
            showDivider={false}
            tabTestIdPrefix="review-step"
            activeId={step}
            onChange={(id) => void goToStep(id as WizardStep)}
            tabs={wizardSteps.map((id) => {
              const done = tabDone[id];
              const active = step === id;
              const locked = id !== "intent" && !intentConfirmed && !resumed;
              return {
                id,
                label: done && !active ? `✓ ${WIZARD_STEP_LABELS[id]}` : WIZARD_STEP_LABELS[id],
                disabled: locked,
                title: locked ? "Confirm the task intent before opening this step" : undefined,
              };
            })}
          />
        </div>

        <div
          className={clsx(
            "flex-1 min-h-0 px-6 py-5",
            step === "artefacts" ? "flex flex-col overflow-hidden" : "overflow-y-auto",
          )}
        >
          {step === "intent" && (
            <IntentPhase
              run={run}
              intentConfirmed={intentConfirmed}
              intentFeedback={intentFeedback}
              onConfirmChange={setIntentConfirmed}
              onFeedbackChange={setIntentFeedback}
            />
          )}

          {step === "orchestrate" && (
            <div className="space-y-8">
              <PhaseInstruction>
                Follow each step in order: what {agentName} thought, any message to the client, then
                the tool it used. Green ticks in the toolbox mean a tool succeeded. Answer every
                question below — say &ldquo;No&rdquo; when something looks wrong.
              </PhaseInstruction>
              <OrchestrationPhase run={run} steps={phases.orchestration} outcome={phases.outcome} />
              <div className="border-t border-white/5 pt-6">
                <p className="text-xs font-medium text-slate-400 mb-4 uppercase tracking-wide">Your rating</p>
                <RatingPanel
                  criteria={criteria.orchestration}
                  answers={ratings.orchestration.answers}
                  checkComments={ratings.orchestration.check_comments}
                  rag={ratings.orchestration.human_rag}
                  note={ratings.orchestration.human_note}
                  onAnswer={setAnswer}
                  onCheckComment={setCheckComment}
                  onOverride={setOverride}
                  onNote={setNote}
                />
              </div>
            </div>
          )}

          {step === "output" && (
            <div className="space-y-8">
              <PhaseInstruction>
                Read the client message and the agent&apos;s decision. Check the summary matches
                reality, then answer every question — &ldquo;done&rdquo; means the agent considers
                the task complete for this step in the workflow.
              </PhaseInstruction>
              <OutcomePhase run={run} steps={phases.orchestration} step={phases.outcome} agentName={agentName} />
              <div className="border-t border-white/5 pt-6">
                <p className="text-xs font-medium text-slate-400 mb-4 uppercase tracking-wide">Your rating</p>
                <RatingPanel
                  criteria={criteria.output}
                  answers={ratings.output.answers}
                  checkComments={ratings.output.check_comments}
                  rag={ratings.output.human_rag}
                  note={ratings.output.human_note}
                  onAnswer={setAnswer}
                  onCheckComment={setCheckComment}
                  onOverride={setOverride}
                  onNote={setNote}
                />
              </div>
            </div>
          )}

          {step === "artefacts" && (
            <ArtefactsPhase
              run={run}
              ratings={artefactRatings}
              onChange={handleArtefactRatingChange}
              aceComparison={aceComparison}
              showAceComparison={showAceFeatures}
              aceLoading={aceLoading}
              activeArtifactId={focusArtefactId}
              focusCheckId={focusCheckId}
            />
          )}
          {step === "submit" && (
            <SummaryScreen
              run={run}
              criteria={criteria}
              ratings={ratings}
              artefactRatings={artefactRatings}
              onEdit={(s) => goToStep(s)}
              onCompareArtefact={showAceFeatures ? goToArtefactCompare : undefined}
              onArtefactApprovalChange={(artifactId, human_approval) =>
                setArtefactRatings((prev) => ({
                  ...prev,
                  [artifactId]: {
                    ...(prev[artifactId] ?? {
                      answers: {},
                      check_comments: {},
                      suggested_rag: null,
                      human_rag: null,
                      human_note: "",
                      human_approval: null,
                    }),
                    human_approval,
                  },
                }))
              }
              submitted={submitted}
              reviewComplete={Boolean(savedReview)}
              needsSave={needsSave}
              canSavePartialArtefacts={Boolean(savedReview)}
              showAceRatings={showAceFeatures}
              showAcePanel={showAceFeatures}
              aceComparison={aceComparison}
              aceLoading={aceLoading}
              aceError={aceError}
              focusCheckId={focusCheckId}
              onRunAce={() => void runAceRating(null, false)}
              onRerunAce={() => void runAceRating(null, true)}
              reviewId={reviewId}
              currentVersion={versionNumber}
              versionRefreshToken={versionRefreshToken}
              showVersionHistory={isAdmin && Boolean(reviewId)}
              catalogueKey={catalogueKey ?? null}
              aceTrialRefreshToken={aceTrialRefreshToken}
              onRunFreshAce={(args) => void runFreshAce(args)}
              onSelectAceTrial={(trialId) => void selectAceTrial(trialId)}
              onPromoteAceTrial={(trialId) => void promoteAceTrial(trialId)}
            />
          )}
        </div>

        {step !== "submit" && !submitted && (
          <div className="flex items-center justify-between border-t border-slate-800/80 px-6 py-4 flex-shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              {step === "intent" ? "Return to list" : "Previous"}
            </button>

            <DisabledActionTooltip
              reasons={!currentComplete || submitting ? nextBlockedReasons : NO_BLOCKED_REASONS}
            >              <Button
                color="indigo"
                disabled={!currentComplete || submitting}
                onClick={() => void handleNext()}
                data-testid="review-next"
              >
                {nextLabels[step] ?? "Next"}
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </DisabledActionTooltip>
          </div>
        )}

        {step === "submit" && (
          <div className="flex items-center justify-between gap-4 border-t border-slate-800/80 px-6 py-4 flex-shrink-0">
            <Button plain onClick={onDone} className="!text-slate-300 hover:!text-white">
              <FontAwesomeIcon icon={faArrowLeft} />
              Return to list
            </Button>

            <div className="flex flex-col items-end gap-2">
              {submitError ? <p className="text-sm text-rose-400">{submitError}</p> : null}
              {reviewSaved ? (
                <Button color="indigo" onClick={onDone} data-testid="review-finish">
                  Finish
                </Button>
              ) : (
                <DisabledActionTooltip
                  reasons={!canSaveReview || submitting ? saveBlockedReasons : NO_BLOCKED_REASONS}
                  heading="Can't submit yet"
                >
                  <Button
                    color="indigo"
                    disabled={!canSaveReview || submitting}
                    onClick={() => void handleSubmit()}
                    data-testid="review-save"
                  >
                    {submitting ? "Saving…" : savedReview ? "Save changes" : "Submit review"}
                  </Button>
                </DisabledActionTooltip>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
